import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import Button from '../../library/button/button';
import FilterChips from '../../library/filter-chips/filter-chips';
import Page, { Content } from '../../library/page/page';
import Pagination from '../../library/pagination/pagination';
import Typography from '../../library/typography/typography';
import FitMessage from '../../components/fit-message/fit-message';
import Message from '../../components/message/message';
import SectionLoadingSpinner from '../../components/section-loading-spinner/section-loading-spinner';
import TireWheelPackageDetails from '../../components/tire-wheel-package-details/tire-wheel-package-details';
import TreadwellSeeAllButton from '../../components/treadwell-see-all-button/treadwell-see-all-button';
import PlpProductBanner from './components/plp-product-banner/plp-product-banner';
import ProductComparePreview from './components/product-compare-preview/product-compare-preview';
import ProductListDrivingProfile from './components/product-list-driving-profile/product-list-driving-profile';
import ProductListFilterModal from './components/product-list-filter-modal/product-list-filter-modal';
import ProductListGrid from './components/product-list-grid/product-list-grid';
import ProductListProfileChips from './components/product-list-profile-chips/product-list-profile-chips';
import ProductListTabs from './components/product-list-tabs/product-list-tabs';
import ResultsBar from './components/results-bar/results-bar';
import SetProductTabs from './components/set-product-tabs/set-product-tabs';
import StaggeredProductTabs from './components/staggered-product-tabs/staggered-product-tabs';
import StickyFilterChips from './components/sticky-filter-chips/sticky-filter-chips';
import TopThree from './components/top-three/top-three';
import TreadwellProductBanner from './components/treadwell-product-banner/treadwell-product-banner';
import WinterTiresBanner from './components/winter-tires-banner/winter-tires-banner';
import WinterToggle from './components/winter-toggle/winter-toggle';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import NoSearchResultsPage from '../no-search-results-page/no-search-results-page';
import {
  FILTERS_CLEARED_QUERY_PARAM_NAME,
  PLP_TAB_BANNER_TYPES
} from './product-list-page-constants';

import {
  useCmsPageContentSlots,
  useCompareProducts,
  useContainerQuery,
  useFetchCompareProducts,
  useFitment,
  useInitialProductListFilterValueLimits,
  useModal,
  useMyAccount,
  useMyStore,
  useNavigateTo,
  usePrevious,
  useProductEntryDataLayerValues,
  useScrollTo,
  useSectionLoadingSpinner,
  useTreadwellForm,
  useTreadwellPersonas,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';
import usePlpAnalytics from './hooks/use-plp-analytics';
import usePlpProducts from './hooks/use-plp-products';
import usePlpTopThree from './hooks/use-plp-top-three';

import { trackAction } from '../../shared/utils/analytics/analytics';
import {
  clearLastPlpProduct,
  clearStoredBackToResultsUrls,
  getStoredBackToResultsUrl,
  removeStoredBackToResultsUrl,
  setStoredBackToResultsUrl
} from '../../shared/utils/back-to-results/back-to-results';
import { isChipFilter } from '../../shared/utils/filters/filters';
import { formatTireSizeParts } from '../../shared/utils/format/format';
import { ProductTypeProp } from '../../shared/utils/prop-types/prop-types';
import { sessionStorage } from '../../shared/utils/storage/storage';
import {
  getIsAppBasedDriver,
  getTreadwellSelectorType
} from '../../shared/utils/treadwell/treadwell';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { getPlpLD } from './utils/item-list-ld';
import { isTrailerOrLawnOrAtvOrUtvCategory } from './utils/product-list-page-category-utils';
import { getFleetInfoMessage } from './utils/product-list-page-util';
import { getResultsText } from './utils/results-text-utils';

import { TireSearchOption } from '../../shared/types/graphql-types';
import { PLPInfoMessageIds } from './types/plp-products';
import { BACK_TO_RESULTS_PAGE_IDS } from '../../shared/constants/back-to-results';
import { PRODUCT_POSITION } from '../../shared/constants/data-layer';
import { FILTER_CATEGORY_CODES } from '../../shared/constants/filters';
import { FIT } from '../../shared/constants/fit';
import {
  FLEET_PRICE_DRAWER,
  SELECT_NEW_STORE_MODAL
} from '../../shared/constants/modals';
import { PageTypes } from '../../shared/constants/page-types';
import {
  PRODUCT_COMPARE_SELECTOR_TYPE,
  PRODUCT_TYPES,
  TREADWELL_PRODUCTS_DISPLAY_TYPES
} from '../../shared/constants/products';
import {
  CURRENT_SEARCH_OPTIONS,
  SORT_PARAMS
} from '../../shared/constants/search-types';
import { LAST_PLP_PRODUCT } from '../../shared/constants/storage-keys';
import { SERVICE_CODE } from '../../shared/constants/store';

import styles from './product-list-page.scss';

const ProductListFilter = lazy(() =>
  import('../../components/product-list-filter/product-list-filter')
);

const ProductCompareDrawer = lazy(() =>
  import('../../components/product-compare-drawer/product-compare-drawer')
);

const TREADWELL_RANK = 'treadwell_recommendation_rank';

function ProductListPage(props) {
  const {
    isAccessorySearch,
    isBrandSearch,
    isFitmentSearch,
    isPackageSearch,
    isPrebuiltPackageSearch,
    isSizeSearch,
    isStaggeredSearch,
    isTextSearch,
    productType,
    queryType
  } = props;

  const history = useHistory();
  const { hash, pathname, search, state } = useLocation();
  const { brand, staggeredSearchAxle } = useParams();
  const baseSort = SORT_PARAMS.BEST_SELLER_ASC;
  const { currentSearchOption, sort: sortParam } = queryString.parse(search);

  const { info: infoMessage } = state || {};
  const navigateTo = useNavigateTo();

  const {
    clearSearchTypes,
    disableTreadwellFlow,
    isTreadwellFlow,
    saveSearchTypes,
    saveTreadwellPreferences,
    treadwellPreferences
  } = useTreadwellPreferences();

  const { getTreadwellPersonaData } = useTreadwellPersonas();

  const { isFitmentLoading, selectedFitment } = useFitment();

  const { isFleetAccount } = useMyAccount();

  const isAppBasedDriver = getIsAppBasedDriver();

  const {
    allProducts,
    allProductsDoesFitEnum,
    category,
    clearSelectedFilters,
    currentSort,
    error,
    filters,
    getPagenationUrl,
    info,
    isLoading,
    metaData,
    numberOfPages,
    onSelectSort,
    pageNum,
    pageSize,
    productSetSearchType,
    products,
    recommendationAvailable,
    searchOption,
    searchText,
    selectFilters,
    selectedFilters,
    setPageNum,
    setPageSize,
    setSelectedFilters,
    setTreadwellProductsDisplayType,
    sortOptions,
    staggeredSearchType,
    tireSizes,
    toggleAllSearchResults,
    totalNumberOfResults,
    treadwellProductsDisplayType,
    wheelSizes
  } = usePlpProducts({
    isAccessorySearch,
    isFitmentLoading,
    isFitmentSearch,
    isPackageSearch,
    isPrebuiltPackageSearch,
    isSizeSearch,
    isStaggeredSearch,
    productType,
    queryType,
    selectedFitment
  });
  const {
    hasFetchedData: topThreeHasFetchedData,
    lowestPriceProduct,
    shouldDisplay: shouldDisplayTopThree,
    tiles: topThreeTiles
  } = usePlpTopThree({
    isBrandSearch,
    productType,
    staggeredSearchType,
    tireSizes
  });

  const { onSelectedCustomDrivingPriorities } = useTreadwellForm(
    { isPackageFlow: isPackageSearch, onZipCodeSubmit: null },
    selectedFitment,
    recommendationAvailable
  );
  const wasLoading = usePrevious(isLoading);

  const onUpdateShowWinterTires = useCallback(() => {
    saveTreadwellPreferences({
      ...treadwellPreferences,
      showWinterTires: !treadwellPreferences?.showWinterTires
    });
  }, [saveTreadwellPreferences, treadwellPreferences]);

  const breakpoint = useContainerQuery();
  const lastPlpProduct = sessionStorage(LAST_PLP_PRODUCT);
  const scrollToHash = useScrollTo(hash, {
    behavior: 'auto',
    offset: 120
  });

  const scrollToPLPTop = useScrollTo(
    breakpoint.isMinSm ? '#product-list' : '#product-list-content',
    {
      behavior: 'auto',
      offset: breakpoint.isMinSm ? 120 : 90
    }
  );

  const isTreadwellSearchOption =
    currentSearchOption === CURRENT_SEARCH_OPTIONS.TREADWELL;

  const [selectedProductBannerTab, setSelectedProductBannerTab] = useState(
    isTreadwellSearchOption || sortParam === SORT_PARAMS.RECOMMENDED
      ? PLP_TAB_BANNER_TYPES.TREADWELL
      : PLP_TAB_BANNER_TYPES.TIRES
  );

  const {
    defaultMaxPrice,
    defaultMinPrice,
    highestPriceInResults,
    lowestPriceInResults,
    onValueChange: onSliderValueChange
  } = useInitialProductListFilterValueLimits(filters);

  const {
    checkCompareProductsPath,
    clearAllProductsForComparison,
    maxProductsForComparison,
    onProductComparison,
    productsForComparison: localProductsForComparison,
    quantities: initialQuantities,
    showCompareDrawer,
    updateShowCompareDrawer
  } = useCompareProducts();
  const { setShowSpinner } = useSectionLoadingSpinner();
  const { updatePlpAnalytics } = usePlpAnalytics(
    products,
    productType,
    selectedFilters,
    sortOptions?.[currentSort]?.name,
    isLoading,
    isPrebuiltPackageSearch,
    pageNum,
    numberOfPages,
    category,
    selectedProductBannerTab,
    filters
  );
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { productData } = useFetchCompareProducts({
    initialQuantities,
    maxProductsForComparison,
    productsForComparison: localProductsForComparison,
    selectedFitment,
    treadwellPreferences
  });
  const { getContentSlot } = useCmsPageContentSlots('buy-tires');

  const productsForComparison =
    localProductsForComparison?.map(comparisonProduct => {
      const selectedProduct =
        productData?.products.find(
          item => item?.code === comparisonProduct?.code
        ) || [];
      return {
        ...comparisonProduct,
        ...selectedProduct
      };
    }) || [];

  const [displayMobileFilters, setDisplayMobileFilters] = useState(false);
  const [isStoreServing, setIsStoreServing] = useState(false);
  const [shouldSuppressNoPackagesModal, setShouldSuppressNoPackagesModal] =
    useState(false);
  const [isBrandSizeSearch, setIsBrandSizeSearch] = useState(
    Boolean(brand || staggeredSearchAxle)
  );

  const { closeModal, isOpen, openModal } = useModal();
  const { myStore } = useMyStore();
  const queryParams = queryString.parse(search);
  const comparePreviewRef = useRef(null);
  const resultsBarRef = useRef(null);
  const filterScrollRef = useRef(0);
  const filterContainerRef = useRef(null);
  const backToResultsContext = getStoredBackToResultsUrl(
    BACK_TO_RESULTS_PAGE_IDS.PLP
  );
  const addTireHref = backToResultsContext?.url || '/fitmentresult/tires';

  function getProductListClassName() {
    let className = `${styles['product-list']} ${styles['grid']}`;

    if (
      selectedFitment &&
      productType === PRODUCT_TYPES.TIRES &&
      isTreadwellFlow
    ) {
      className += ` ${styles['treadwell']}`;
    }

    return className;
  }

  useEffect(() => {
    if (!isLoading) {
      setSelectedProductBannerTab(
        isTreadwellSearchOption
          ? PLP_TAB_BANNER_TYPES.TREADWELL
          : PLP_TAB_BANNER_TYPES.TIRES
      );
    }
  }, [isTreadwellSearchOption, isLoading]);

  useEffect(() => {
    setShowSpinner(isLoading);
  }, [isLoading, setShowSpinner]);

  useEffect(() => {
    getTreadwellPersonaData(selectedFitment).then(({ prioritiesList }) => {
      if (isArrayWithLength(prioritiesList)) {
        onSelectedCustomDrivingPriorities(prioritiesList);
      }
    });
  }, [
    getTreadwellPersonaData,
    onSelectedCustomDrivingPriorities,
    selectedFitment
  ]);

  useEffect(() => {
    const isNewStoreModalOpen =
      !isLoading &&
      productType === PRODUCT_TYPES.TIRES &&
      (selectedFitment?.frontWheelDiameter === '19.5' ||
        selectedFitment?.rearWheelDiameter === '19.5') &&
      !myStore?.additionalServices.some(
        service => service.code === SERVICE_CODE.COMMERCIAL_VEHICLE
      );
    setIsStoreServing(isNewStoreModalOpen);
    if (isNewStoreModalOpen) {
      openModal(SELECT_NEW_STORE_MODAL, { myStore });
    }
  }, [isLoading, myStore, openModal, productType, selectedFitment]);

  useEffect(() => {
    if (
      !isLoading &&
      !isOpen &&
      !isArrayWithLength(products) &&
      !shouldSuppressNoPackagesModal &&
      isPackageSearch
    ) {
      setShouldSuppressNoPackagesModal(true);
      navigateTo('#/package/no-package-results');
    }
  }, [
    isLoading,
    isOpen,
    isPackageSearch,
    navigateTo,
    products,
    shouldSuppressNoPackagesModal
  ]);

  useEffect(() => {
    if (!isLoading && info) {
      const newLocation = {
        ...history.location,
        state: {
          info
        }
      };

      if (
        info.id === PLPInfoMessageIds.noInStockTreadwellProducts &&
        search.includes('storeInStock%3AIn%20Stock')
      ) {
        newLocation.search = search.replace(/q=.+&/, 'q=%3Arecommended&');
        history.replace(newLocation);
      } else if (
        info.id === PLPInfoMessageIds.noTreadwellProducts &&
        search.includes('%3Arecommended')
      ) {
        newLocation.search = '';
        history.replace(newLocation);

        disableTreadwellFlow();
      }
    }
  }, [isLoading, info, history, search, disableTreadwellFlow]);

  useEffect(() => {
    if (lastPlpProduct && !hash.includes(lastPlpProduct)) {
      history.replace({
        ...history.location,
        hash: `#product-code-${lastPlpProduct}`
      });
      clearLastPlpProduct();
    }
  }, [hash, history, lastPlpProduct]);

  useEffect(() => {
    if (hash && hash.includes('product-code') && !isLoading) {
      setTreadwellProductsDisplayType(
        TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
      );
      scrollToHash();
      clearLastPlpProduct();
    }
  }, [scrollToHash, hash, setTreadwellProductsDisplayType, isLoading]);

  useEffect(() => {
    if (
      isPrebuiltPackageSearch &&
      !isLoading &&
      !isArrayWithLength(products) &&
      !hash
    ) {
      navigateTo('#/package/choose-tire-prebuilt-entry/no-results/home');
    }
  }, [
    hash,
    closeModal,
    navigateTo,
    openModal,
    isPrebuiltPackageSearch,
    isLoading,
    products
  ]);

  function onToggleMobileFilter() {
    setDisplayMobileFilters(!displayMobileFilters);
  }

  function onPageChange(pageNum) {
    scrollToPLPTop();
    clearLastPlpProduct();
    setPageNum(pageNum);
  }

  function onPageSizeChange(newPageSize) {
    scrollToPLPTop();
    clearLastPlpProduct();
    setPageSize(newPageSize);
  }

  const handleFilterScroll = () => {
    if (filterContainerRef?.current?.scrollTop) {
      filterScrollRef.current = filterContainerRef?.current?.scrollTop;
    }
  };

  function updateSelectedFilters(filterToToggle) {
    clearLastPlpProduct();
    selectFilters(filterToToggle);
    setTreadwellProductsDisplayType(TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL);
    setIsBrandSizeSearch(false);

    if (
      !selectedFilters.find(
        selectedFilter =>
          selectedFilter?.categoryCode === filterToToggle?.categoryCode
      )
    ) {
      trackAction('plp_filter_click', {
        filter_family: filterToToggle?.categoryName,
        filter_selection: filterToToggle?.valueName
      });
    }
  }

  function getTrackProductPayload(product, idx, quantity, rearQuantity) {
    const pos = `${idx + 1}`;

    return {
      ...getProductEntryDataLayerValues(product, quantity, rearQuantity),
      [PRODUCT_POSITION]: product?.rearProduct ? [pos, pos] : [pos]
    };
  }

  const isTires = productType === PRODUCT_TYPES.TIRES;

  const isTreadwellTabSelected =
    selectedProductBannerTab === PLP_TAB_BANNER_TYPES.TREADWELL;
  const showProductList = !(isTreadwellTabSelected && !treadwellPreferences);

  const isTrailerOrLawnOrAtvOrUtvSearch = isTrailerOrLawnOrAtvOrUtvCategory(
    category?.code
  );

  function onAddProductClick(product, idx, quantity, rearQuantity) {
    const trackProductPayload = getTrackProductPayload(
      product,
      idx,
      quantity,
      rearQuantity
    );

    if (isTreadwellFlow && isTreadwellTabSelected) {
      trackAction('product_list_add_treadwell_recommendation', {
        [TREADWELL_RANK]: product?.recommendationData?.rank,
        trackProductPayload
      });
    } else {
      trackAction('product_list_cart_add', trackProductPayload);
    }
  }

  function onViewProductClick(product, idx) {
    const trackProductPayload = getTrackProductPayload(product, idx);

    const treadwellSelectorType = getTreadwellSelectorType(
      treadwellPreferences?.showWinterTires
    );
    const compareSelectorType = product?.recommendationData
      ? treadwellSelectorType
      : PRODUCT_COMPARE_SELECTOR_TYPE.ALL_TIRES;
    saveSearchTypes({
      code: product?.code,
      compareSelectorType: compareSelectorType,
      shouldCheckExisting: false
    });

    if (isTreadwellFlow && isTreadwellTabSelected) {
      trackProductPayload[TREADWELL_RANK] = product?.recommendationData?.rank;
      trackAction(
        'product_list_select_treadwell_recommendation',
        trackProductPayload
      );
    } else {
      trackAction('product_view_details_click', trackProductPayload);
    }
  }

  function handleCloseCompareDrawer(
    trackActionId = 'product_list_compare_drawer_close'
  ) {
    updateShowCompareDrawer(false);
    trackAction(trackActionId);
  }

  const onTabClick = useCallback(
    (currentTab, selectedTab) => {
      if (currentTab !== selectedTab) {
        clearAllProductsForComparison();
        clearSearchTypes();
      }
    },
    [clearAllProductsForComparison, clearSearchTypes]
  );

  const plpLD = getPlpLD(
    products,
    pathname,
    metaData?.description,
    metaData?.title
  );

  useEffect(() => {
    checkCompareProductsPath(productType, pathname);
  }, [checkCompareProductsPath, productType, pathname]);

  useEffect(() => {
    if (searchOption === TireSearchOption.All && isTreadwellFlow) {
      isTreadwellTabSelected
        ? setTreadwellProductsDisplayType(
            TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
          )
        : setTreadwellProductsDisplayType(
            TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
          );
    }
  }, [
    searchOption,
    setTreadwellProductsDisplayType,
    isTreadwellTabSelected,
    isTreadwellFlow
  ]);

  useEffect(() => {
    if (wasLoading && !isLoading && filterScrollRef.current) {
      setTimeout(() => {
        filterContainerRef.current?.scrollTo({
          behavior: 'smooth',
          top: filterScrollRef.current
        });
      }, 300);
    }
  }, [isLoading, wasLoading, filterScrollRef]);

  if (error && !products && !window.isPreRendering) {
    throw error;
  }

  if (
    !isLoading &&
    !error &&
    !window.isPreRendering &&
    !isArrayWithLength(products)
  ) {
    return <NoSearchResultsPage />;
  }

  clearStoredBackToResultsUrls();
  setStoredBackToResultsUrl(BACK_TO_RESULTS_PAGE_IDS.PLP);

  const showTopThree =
    showProductList &&
    productType === PRODUCT_TYPES.TIRES &&
    !isBrandSearch &&
    !isFleetAccount &&
    !isSizeSearch &&
    !isTreadwellTabSelected &&
    selectedFitment &&
    (!recommendationAvailable ||
      selectedFilters.some(
        filter => filter.categoryCode === 'all-tire-boolean'
      ));
  const formattedTireSize = formatTireSizeParts(tireSizes?.front);

  const isProductListFilterModalOpen =
    filters && Boolean(displayMobileFilters) && breakpoint.isSM;

  const isFleetEnabled =
    isFleetAccount && process.env.__FF_FLEET_ACCOUNT__ === 'true';
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  let fleetInfoMessage = '';
  if (isFleetEnabled) {
    fleetInfoMessage = getFleetInfoMessage({
      filters,
      products,
      selectedFilters
    });
  }

  const selectedFleetFilter = selectedFilters?.filter(
    filter =>
      filter.categoryCode === FILTER_CATEGORY_CODES.FLEET_PREFERRED_PRODUCTS
  );

  return (
    <DefaultLayout showMinimalFooter>
      {showCompareDrawer && (
        <Suspense fallback={<div />}>
          <ProductCompareDrawer
            isFitmentSearch={isFitmentSearch}
            isOpen={showCompareDrawer}
            isPackageSearch={isPackageSearch}
            isSizeSearch={isSizeSearch}
            isStaggeredSearch={isStaggeredSearch}
            onAddToCompare={handleCloseCompareDrawer}
            onClose={handleCloseCompareDrawer}
            onInsufficientProductCount={() => {
              removeStoredBackToResultsUrl();
              navigateTo(addTireHref);
            }}
            pageType={PageTypes.PLP}
            productType={productType}
            queryType={queryType}
          />
        </Suspense>
      )}
      <Page className={styles['page']}>
        <Helmet>
          <title>
            {pageNum > 0 && numberOfPages > 0
              ? `Page ${pageNum + 1} of ${numberOfPages} for `
              : ''}
            {metaData?.title || ''}
          </title>
          {isBrandSearch && queryParams.cat && (
            <link
              href={`${window.location.protocol}//${process.env.__DT_HOST__}${window.location.pathname}${window.location.search}`}
              rel="canonical"
            />
          )}
          <meta content={metaData?.description} name="description" />
          {pageNum < numberOfPages - 1 && (
            <link href={getPagenationUrl(pageNum + 1)} rel="next" />
          )}
          {pageNum > 0 && (
            <link href={getPagenationUrl(pageNum - 1)} rel="prev" />
          )}
          <meta content={metaData?.keywords} name="keywords" />
          {plpLD && !window.isPreRendering && (
            <script type="application/ld+json">{plpLD}</script>
          )}
        </Helmet>
        {!isLoading && productsForComparison?.length > 0 && (
          <ProductComparePreview
            isFitmentSearch={isFitmentSearch}
            isPackageSearch={isPackageSearch}
            isSizeSearch={isSizeSearch}
            isStaggeredSearch={isStaggeredSearch}
            maxProductsForComparison={maxProductsForComparison}
            onClearAll={() => {
              clearAllProductsForComparison();
              clearSearchTypes();
            }}
            onClearProduct={onProductComparison}
            pageType={PageTypes.PLP}
            productType={productType}
            productsForComparison={productsForComparison}
            queryType={queryType}
            ref={comparePreviewRef}
            updateShowCompareDrawer={updateShowCompareDrawer}
          />
        )}

        <Content className={styles['content-grid']} id="product-list-content">
          {showTopThree && !breakpoint.isMinMd && (
            <TopThree
              hasFetchedData={topThreeHasFetchedData}
              shouldDisplay={shouldDisplayTopThree}
              tiles={topThreeTiles}
            />
          )}

          <div className={getProductListClassName()}>
            {breakpoint.isMinSm && (
              <div className={`${styles['left-column']} ${styles['grid']}`}>
                <div
                  className={`${styles['product-list-filter']} ${styles['grid']}`}
                  id="product-list-filter"
                  onScroll={handleFilterScroll}
                  ref={filterContainerRef}
                >
                  {breakpoint.isMinSm && !isTrailerOrLawnOrAtvOrUtvSearch && (
                    <ProductListDrivingProfile
                      onWinterToggle={onUpdateShowWinterTires}
                      showTreadwellProfile={
                        isTreadwellTabSelected && isTreadwellFlow
                      }
                    />
                  )}

                  <Suspense fallback={<div />}>
                    {filters && !window.isPreRendering && showProductList && (
                      <ProductListFilter
                        accessoryCategory={category?.code}
                        defaultMaxPrice={defaultMaxPrice}
                        defaultMinPrice={defaultMinPrice}
                        filterContainerRef={filterContainerRef}
                        filters={filters}
                        highestPriceInResults={highestPriceInResults}
                        isAccessorySearch={isAccessorySearch}
                        isLoadingPLP={isLoading}
                        isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                        isTextSearch={isTextSearch}
                        lowestPriceInResults={lowestPriceInResults}
                        onSelectFilter={updateSelectedFilters}
                        onSliderValueChange={onSliderValueChange}
                        selectedFilters={selectedFilters}
                        toggleType="CHEVRON"
                      />
                    )}
                  </Suspense>
                </div>
              </div>
            )}

            <div
              className={`${styles['right-column']} ${styles['grid']}`}
              id="product-list"
            >
              {isProductListFilterModalOpen && (
                <ProductListFilterModal
                  categoryCode={category?.code}
                  defaultMaxPrice={defaultMaxPrice}
                  defaultMinPrice={defaultMinPrice}
                  filters={filters}
                  highestPriceInResults={highestPriceInResults}
                  isAccessorySearch={isAccessorySearch}
                  isOpen
                  isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                  isTextSearch={isTextSearch}
                  lowestPriceInResults={lowestPriceInResults}
                  onClearSelectedFilters={clearSelectedFilters}
                  onClose={onToggleMobileFilter}
                  onSliderValueChange={onSliderValueChange}
                  onUpdateSelectedFilters={updateSelectedFilters}
                  selectedFilters={selectedFilters}
                />
              )}

              <div className={styles['inner']}>
                <div className={styles['top-section']}>
                  {isTires &&
                    !isPrebuiltPackageSearch &&
                    !isTrailerOrLawnOrAtvOrUtvSearch && (
                      <ProductListTabs
                        clearSelectedFilters={() =>
                          setSelectedFilters(
                            isFleetEnabled
                              ? selectedFleetFilter
                              : selectedFilters
                          )
                        }
                        onShopTiresCTAClick={toggleAllSearchResults}
                        selectedProductBannerTab={selectedProductBannerTab}
                        setSelectedProductBannerTab={
                          setSelectedProductBannerTab
                        }
                      />
                    )}
                  {isTreadwellTabSelected ? (
                    <>
                      {(breakpoint.isMinMd || !showProductList) && (
                        <TreadwellProductBanner
                          lowestPriceProduct={lowestPriceProduct}
                          productCount={totalNumberOfResults}
                          productType={productType}
                        />
                      )}

                      {showProductList && !breakpoint.isMinMd && (
                        <Typography
                          className={styles['mobile-banner']}
                          data-testid="product-list-page_mobile-banner"
                          weight="black"
                        >
                          <div className={styles['mobile-banner-header']}>
                            <div className={styles['result-count']}>
                              {totalNumberOfResults} perfect tire matches{' '}
                              {lowestPriceProduct?.price ? (
                                <span className={styles['lowest-price-from']}>
                                  {'for you, '}
                                  from{' '}
                                  <strong>${lowestPriceProduct.price}</strong>
                                </span>
                              ) : (
                                'for you'
                              )}
                            </div>
                            {!(isFleetTreadwell || isAppBasedDriver) && (
                              <WinterToggle
                                onToggle={onUpdateShowWinterTires}
                              />
                            )}
                          </div>
                          <FitMessage
                            data-testid="FitMessage"
                            doesFit={allProductsDoesFitEnum === FIT}
                            fitment={selectedFitment}
                            infoMessage={infoMessage}
                            isLoading={isLoading}
                            isPackage={isPrebuiltPackageSearch}
                            productType={productType}
                          />
                          <ProductListProfileChips
                            showCollapsibleFitmentBanner={!breakpoint.isMinSm}
                            showTreadwellProfile={
                              isTreadwellTabSelected && isTreadwellFlow
                            }
                          />
                        </Typography>
                      )}
                    </>
                  ) : (
                    <PlpProductBanner
                      brandName={
                        isBrandSizeSearch
                          ? brand || staggeredSearchAxle
                          : category?.name
                      }
                      brandSeoName={isBrandSearch ? category?.seoName : ''}
                      doesFitEnum={allProductsDoesFitEnum}
                      infoMessage={infoMessage}
                      isBrandSearch={isBrandSearch}
                      isBrandSizeSearch={isBrandSizeSearch}
                      isFitmentSearch={isFitmentSearch}
                      isLoading={!allProductsDoesFitEnum}
                      isPackageSearch={isPackageSearch}
                      isPrebuiltPackageSearch={isPrebuiltPackageSearch}
                      isSizeSearch={isSizeSearch}
                      isTrailerOrLawnOrAtvOrUtvSearch={
                        isTrailerOrLawnOrAtvOrUtvSearch
                      }
                      lowestPriceProduct={lowestPriceProduct}
                      productType={productType}
                      tireSizes={tireSizes}
                      totalNumberOfResults={totalNumberOfResults}
                      wheelSizes={wheelSizes}
                    />
                  )}

                  {showTopThree && breakpoint.isMinMd && (
                    <TopThree
                      hasFetchedData={topThreeHasFetchedData}
                      shouldDisplay={shouldDisplayTopThree}
                      tiles={topThreeTiles}
                    />
                  )}

                  {productType === PRODUCT_TYPES.TIRES &&
                    breakpoint.isMinSm &&
                    showProductList &&
                    !isTreadwellFlow && (
                      <WinterTiresBanner
                        filters={filters}
                        formattedTireSize={formattedTireSize}
                        selectedFilters={selectedFilters}
                      />
                    )}

                  {showProductList && (
                    <>
                      {isPackageSearch && <TireWheelPackageDetails />}
                      <div
                        className={
                          styles[
                            `${
                              fleetInfoMessage
                                ? 'container-fleet-info-message'
                                : ''
                            }`
                          ]
                        }
                      >
                        <ResultsBar
                          currentSort={isLoading ? baseSort : currentSort}
                          doesFitEnum={allProductsDoesFitEnum}
                          filterCount={selectedFilters?.length}
                          hidePageSize={
                            process.env.__FF_PLP_PAGE_SIZE_DROPDOWN__ !==
                              'true' ||
                            isLoading ||
                            !breakpoint.isMinMd ||
                            isPrebuiltPackageSearch ||
                            treadwellProductsDisplayType ===
                              TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
                          }
                          infoMessage={infoMessage}
                          isLoading={!allProductsDoesFitEnum}
                          isPackage={isPrebuiltPackageSearch}
                          onPageSizeChange={onPageSizeChange}
                          onSelectSort={sort => onSelectSort(sort)}
                          onToggleMobileFilter={onToggleMobileFilter}
                          pageSize={pageSize}
                          productType={productType}
                          resultsText={
                            isLoading
                              ? null
                              : getResultsText(
                                  products.length,
                                  searchText,
                                  isPrebuiltPackageSearch,
                                  productType
                                )
                          }
                          selectedFitment={selectedFitment}
                          showCount={breakpoint.isMinMd}
                          showFilter={!breakpoint.isMinMd}
                          showFitmentMsg={
                            isTrailerOrLawnOrAtvOrUtvSearch ||
                            !breakpoint.isMinMd
                          }
                          sorts={
                            isLoading || window.isPreRendering
                              ? {
                                  baseSort: {
                                    label: ' ',
                                    value: ''
                                  }
                                }
                              : sortOptions
                          }
                          totalNumberOfResults={
                            isLoading ? ' ' : totalNumberOfResults
                          }
                        />

                        {fleetInfoMessage && isFleetEnabled && (
                          <div className={styles['info-message-section']}>
                            <Message
                              className={styles['info-message']}
                              data-testid="filter-info-message"
                              isSolidInfo
                              message={
                                <>
                                  {fleetInfoMessage}
                                  <Button
                                    onClick={() =>
                                      openModal(FLEET_PRICE_DRAWER)
                                    }
                                    size="regular"
                                  >
                                    &nbsp;Fleet Pricing & Preferences
                                  </Button>
                                </>
                              }
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {breakpoint.isMinSm &&
                    productType === PRODUCT_TYPES.TIRES &&
                    (!isTreadwellTabSelected || isTreadwellFlow) && (
                      <div
                        className={styles['installed-today']}
                        dangerouslySetInnerHTML={{
                          __html: getContentSlot('plp-installed-today')
                        }}
                      />
                    )}

                  {productType === PRODUCT_TYPES.TIRES &&
                    !breakpoint.isMinSm &&
                    showProductList &&
                    !isTreadwellFlow && (
                      <WinterTiresBanner
                        filters={filters}
                        formattedTireSize={formattedTireSize}
                        selectedFilters={selectedFilters}
                      />
                    )}
                  {isStaggeredSearch &&
                    !isPackageSearch &&
                    !isLoading &&
                    (!isArrayWithLength(products) ||
                      search === `?${FILTERS_CLEARED_QUERY_PARAM_NAME}`) && (
                      <Message
                        isWarning
                        message={
                          search === `?${FILTERS_CLEARED_QUERY_PARAM_NAME}`
                            ? 'Your previous search filter(s) has been removed due to no results found.'
                            : 'There are no sets found for the size entered. Please try another size.'
                        }
                      />
                    )}
                </div>
                {isArrayWithLength(selectedFilters) && !breakpoint.isMinSm && (
                  <StickyFilterChips
                    clearSelectedFilters={clearSelectedFilters}
                    onChipClick={updateSelectedFilters}
                    onToggleMobileFilter={onToggleMobileFilter}
                    resultsBarRef={resultsBarRef}
                    selectedFilters={selectedFilters}
                  />
                )}

                {!breakpoint.isMinSm &&
                  productType === PRODUCT_TYPES.TIRES &&
                  (!isTreadwellTabSelected || isTreadwellFlow) && (
                    <div
                      className={styles['installed-today']}
                      dangerouslySetInnerHTML={{
                        __html: getContentSlot('plp-installed-today')
                      }}
                    />
                  )}

                {isStaggeredSearch && !isPackageSearch && showProductList && (
                  <StaggeredProductTabs
                    isSizeSearch={isSizeSearch}
                    onTabClick={onTabClick}
                    productType={productType}
                    staggeredSearchType={staggeredSearchType}
                    tireSizes={tireSizes}
                    wheelSizes={wheelSizes}
                  />
                )}

                {productSetSearchType && (
                  <SetProductTabs
                    doesFitEnum={allProductsDoesFitEnum}
                    productSetSearchType={productSetSearchType}
                  />
                )}

                {Array.isArray(selectedFilters) &&
                  selectedFilters.length > 0 &&
                  breakpoint.isMinSm && (
                    <FilterChips
                      className={styles['filter-chips']}
                      filters={selectedFilters?.filter(isChipFilter)}
                      onChipClick={updateSelectedFilters}
                      onClearAllClick={clearSelectedFilters}
                      showClearAll
                    />
                  )}

                {showProductList && !isStoreServing && (
                  <SectionLoadingSpinner isActive shouldSpinOnRender>
                    <div className={styles['list-wrapper']}>
                      <ProductListGrid
                        isTreadwellTabSelected={isTreadwellTabSelected}
                        onAddProductClick={onAddProductClick}
                        onViewProductClick={onViewProductClick}
                        prebuiltPackages={
                          isPrebuiltPackageSearch ? products : undefined
                        }
                        productSetSearchType={productSetSearchType}
                        products={
                          !isPrebuiltPackageSearch ? products : undefined
                        }
                      />
                    </div>
                  </SectionLoadingSpinner>
                )}
              </div>
              {isTreadwellFlow && (
                <TreadwellSeeAllButton
                  className={styles['recommended-tires-wrapper']}
                  onClick={() => {
                    setTreadwellProductsDisplayType(
                      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
                    );
                    updatePlpAnalytics(allProducts, false);
                  }}
                  treadwellProductsDisplayType={treadwellProductsDisplayType}
                />
              )}
              {numberOfPages > 0 &&
                !window.isPreRendering &&
                showProductList &&
                !isStoreServing && (
                  <Pagination
                    className={styles['pagination']}
                    currentPage={pageNum}
                    numberOfPages={numberOfPages}
                    onPageChange={onPageChange}
                  />
                )}
            </div>
          </div>
        </Content>
      </Page>
    </DefaultLayout>
  );
}

ProductListPage.propTypes = {
  isAccessorySearch: PropTypes.bool,
  isBrandSearch: PropTypes.bool,
  isFitmentSearch: PropTypes.bool,
  isPackageSearch: PropTypes.bool,
  isPrebuiltPackageSearch: PropTypes.bool,
  isSizeSearch: PropTypes.bool,
  isStaggeredSearch: PropTypes.bool,
  isTextSearch: PropTypes.bool,
  productType: ProductTypeProp,
  queryType: PropTypes.string
};

export default ProductListPage;
